import { apiClient, type ApiResponse } from "./apiClient";
import {
  mockDeleteAutoTaggingPlan,
  mockFetchAutoTaggingPlans,
  mockFetchConfigurableConcepts,
  mockFetchDefaultPrompt,
  mockPatchAutoTaggingPlan,
  mockPatchAutoTaggingPlanActive,
  mockTriggerAutoTaggingBundleTask,
} from "./autoTaggingMockData";

/** Flip to true only while the Regulations List dialog uses local mock data. */
export const USE_AUTO_TAGGING_MOCK = false;

export const AI_PLAN_DEFAULT_OBJECT_TYPE = "Insight";
export const AI_PLAN_DEFAULT_OBJECT_TYPE_CODE = "insight";
export const AI_PLAN_DEFAULT_SUB_TYPE_CODE = "regulation";

/** Named type/sub-type option from per-tenant feature flags (`code` for API, `name` for UI). */
export interface AutoTaggingPlanNamedOption {
  code: string;
  name: string;
}

export interface AutoTaggingPlanTypeOption {
  code: string;
  name: string;
  objectSubTypes: AutoTaggingPlanNamedOption[];
}

/** Normalize UI/API type values to snake_case API codes (e.g. "Insight" → "insight"). */
export function formatPlanTypeLabelToCode(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join("_");
}

/** @deprecated Prefer API `name`; kept as legacy fallback when name is missing. */
export function formatPlanTypeCode(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return "";

  return trimmed
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function normalizePlanTypeApiCode(value: string): string {
  return formatPlanTypeLabelToCode(value);
}

function uniqueNormalizedCodes(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const value of values) {
    const code = normalizePlanTypeApiCode(value);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

/**
 * Resolve `target_type` values for dimension/concept APIs.
 * Object Type wins; if only Sub-Type is selected, map it back to parent type(s).
 */
export function resolveConceptQueryTargetTypes(options: {
  objectType?: string | null;
  objectSubType?: string | null;
  planTypes: readonly AutoTaggingPlanTypeOption[];
  fallbackTypeCodes?: readonly string[];
}): string[] {
  const objectType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (objectType) return [objectType];

  const objectSubType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (objectSubType) {
    const matched: string[] = [];
    const seen = new Set<string>();
    for (const planType of options.planTypes) {
      const typeCode = normalizePlanTypeApiCode(planType.code);
      if (!typeCode || seen.has(typeCode)) continue;
      const belongsToType = planType.objectSubTypes.some(
        (item) => normalizePlanTypeApiCode(item.code) === objectSubType,
      );
      if (!belongsToType) continue;
      seen.add(typeCode);
      matched.push(typeCode);
    }
    if (matched.length > 0) return matched;
  }

  return uniqueNormalizedCodes(options.fallbackTypeCodes ?? []);
}

/** Query params for configurable-concepts / output-concepts. */
export function buildConceptQueryParams(
  targetType: string,
  subType?: string | null,
): { target_type: string; sub_type?: string } | null {
  const target_type = normalizePlanTypeApiCode(targetType);
  if (!target_type) return null;
  const sub_type = normalizePlanTypeApiCode(subType ?? "");
  return sub_type ? { target_type, sub_type } : { target_type };
}

function normalizeNamedTypeOption(raw: unknown): AutoTaggingPlanNamedOption | null {
  if (typeof raw === "string") {
    const code = normalizePlanTypeApiCode(raw);
    if (!code) return null;
    return { code, name: formatPlanTypeCode(code) };
  }

  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = normalizePlanTypeApiCode(
    String(record.code ?? record.value ?? "").trim(),
  );
  if (!code) return null;

  const name = String(record.name ?? record.label ?? "").trim() || formatPlanTypeCode(code);
  return { code, name };
}

function normalizeSubTypes(raw: unknown): AutoTaggingPlanNamedOption[] {
  if (!Array.isArray(raw)) return [];

  const subTypes: AutoTaggingPlanNamedOption[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const option = normalizeNamedTypeOption(item);
    if (!option || seen.has(option.code)) continue;
    seen.add(option.code);
    subTypes.push(option);
  }

  return subTypes;
}

function normalizePlanTypeItem(raw: unknown): AutoTaggingPlanTypeOption | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = normalizePlanTypeApiCode(
    String(
      record.code ??
        record.object_type ??
        record.objectType ??
        record.type ??
        "",
    ).trim(),
  );
  if (!code) return null;

  const name =
    String(record.name ?? record.label ?? "").trim() || formatPlanTypeCode(code);
  const objectSubTypes = normalizeSubTypes(
    record.sub_types ??
      record.subTypes ??
      record.object_sub_types ??
      record.objectSubTypes,
  );

  return { code, name, objectSubTypes };
}

function extractPlanTypesPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["types", "items", "object_types", "objectTypes"] as const;

  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }

  const nested = record.data;
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === "object") {
    const nestedRecord = nested as Record<string, unknown>;
    for (const key of nestedKeys) {
      if (Array.isArray(nestedRecord[key])) return nestedRecord[key] as unknown[];
    }
  }

  return [];
}

export function normalizeAutoTaggingPlanTypes(
  payload: unknown,
): AutoTaggingPlanTypeOption[] {
  return extractPlanTypesPayload(payload)
    .map(normalizePlanTypeItem)
    .filter((item): item is AutoTaggingPlanTypeOption => item !== null);
}

export function resolveDefaultObjectType(
  planTypes: AutoTaggingPlanTypeOption[],
): string {
  const matched = planTypes.find(
    (item) => item.code === AI_PLAN_DEFAULT_OBJECT_TYPE_CODE,
  );
  return matched?.code ?? planTypes[0]?.code ?? "";
}

export function getSubTypesForObjectType(
  planTypes: AutoTaggingPlanTypeOption[],
  objectType: string,
): AutoTaggingPlanNamedOption[] {
  const code = normalizePlanTypeApiCode(objectType);
  return planTypes.find((item) => item.code === code)?.objectSubTypes ?? [];
}

export function resolvePlanTypeDisplayName(
  planTypes: AutoTaggingPlanTypeOption[],
  typeCode: string,
): string {
  const code = normalizePlanTypeApiCode(typeCode);
  if (!code) return "";
  return planTypes.find((item) => item.code === code)?.name ?? typeCode.trim();
}

export function resolvePlanSubTypeDisplayName(
  planTypes: AutoTaggingPlanTypeOption[],
  typeCode: string,
  subTypeCode: string,
): string {
  const code = normalizePlanTypeApiCode(subTypeCode);
  if (!code) return "";
  const subTypes = getSubTypesForObjectType(planTypes, typeCode);
  return subTypes.find((item) => item.code === code)?.name ?? subTypeCode.trim();
}

export function buildDefaultPlanItemSelection(
  planTypes: AutoTaggingPlanTypeOption[],
): { objectType: string; objectSubType: string } {
  const objectType = resolveDefaultObjectType(planTypes);
  const objectSubTypes = getSubTypesForObjectType(planTypes, objectType);
  return {
    objectType,
    objectSubType: objectSubTypes[0]?.code ?? "",
  };
}

export async function fetchAutoTaggingPlanTypes(): Promise<
  AutoTaggingPlanTypeOption[]
> {
  const result = await apiClient.get<unknown>("/auto-tagging/plans/types");
  if (result.status !== "success") {
    return [];
  }

  return normalizeAutoTaggingPlanTypes(result.data);
}

export function normalizeAutoTaggingEnabled(payload: unknown): boolean {
  if (typeof payload === "boolean") return payload;
  if (!payload || typeof payload !== "object") return false;
  const record = payload as Record<string, unknown>;
  return record.enabled === true;
}

export async function fetchAutoTaggingEnabled(): Promise<boolean> {
  try {
    const result = await apiClient.get<unknown>("/auto-tagging/enabled");
    if (result.status !== "success") {
      return false;
    }
    return normalizeAutoTaggingEnabled(result.data);
  } catch (error) {
    console.error("Failed to fetch auto-tagging enabled status:", error);
    return false;
  }
}

export async function updateAutoTaggingEnabled(
  enabled: boolean,
): Promise<boolean> {
  const result = await apiClient.put<unknown>("/auto-tagging/enabled", {
    enabled,
  });
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${enabled ? "enable" : "disable"} auto-tagging.`;
    throw new Error(message);
  }

  if (result.data !== undefined && result.data !== null) {
    return normalizeAutoTaggingEnabled(result.data);
  }
  return enabled;
}

const CONCEPT_CODE_TO_LABEL: Record<string, string> = {
  ProductType: "Product Type",
  IngredientName: "Ingredient",
  Ingredient: "Ingredient",
  TherapeuticArea: "Therapeutic Area",
  HealthAuthority: "Health Authority",
  Department: "Department",
};

export function formatConceptCodeToLabel(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return "";
  return CONCEPT_CODE_TO_LABEL[trimmed] ?? trimmed;
}

export interface ConfigurableConcept {
  code: string;
  name: string;
  /** Regulations List's display label compatibility field. */
  display_name?: string;
}

export interface ConceptValue {
  code: string;
  name: string;
  description?: string;
}

/** Reference dimension item on a plan (Doc Type etc.) — Start dialog R1/R2 labels. */
export type AutoTaggingPlanReferenceItem = {
  concept: string;
  display_name?: string;
  all_values: boolean;
  values: ConceptValue[];
};

export interface AutoTaggingPlan {
  /** Prefixed plan id from API (e.g. "TAG12"); keep as string end-to-end. */
  id: string;
  target_type: string;
  sub_type?: string;
  concept: string;
  concept_display_name?: string;
  all_values: boolean;
  concept_values: ConceptValue[];
  prompt: string;
  active: boolean;
  /** Assessment Name — API `name`. */
  name?: string;
  /** Assessment Template — API `assessment_type` (e.g. tagging, implementation). */
  assessment_type?: string;
  /** Reference object filters for Reference Items column. */
  reference_items?: AutoTaggingPlanReferenceItem[];
}

/** UI labels for Assessment Template filter / column. */
export const ASSESSMENT_TEMPLATE_FILTER_OPTIONS = [
  "Implementation",
  "Tagging",
] as const;

export type AssessmentTemplateFilterOption =
  (typeof ASSESSMENT_TEMPLATE_FILTER_OPTIONS)[number];

export interface AutoTaggingBundleTaskRegulation {
  regulation_id: string;
  version: string;
}

export interface AutoTaggingBundleTaskRequest {
  regulations: AutoTaggingBundleTaskRegulation[];
  /** Echo plan ids as received (string); backend also accepts legacy numeric ids. */
  plan_ids: string[];
  force_update: boolean;
}

export interface AutoTaggingBundleTaskResponse {
  uuid: string;
  sub_tasks: string[];
  completed_tasks: string[];
  status: string;
  trigger_by: string;
  force_update: boolean;
  total_tasks: number;
  executed_at: string;
  completed_at?: string;
}

export interface PatchAutoTaggingPlanPayload {
  concept?: string;
  all_values?: boolean;
  concept_values?: ConceptValue[];
  prompt?: string;
}

export const AUTO_TAGGING_START_SUCCESS_TOAST =
  "Assessment started successfully. Please check back shortly for results.";
export const AUTO_TAGGING_START_FAILED_TOAST =
  "Assessment failed to start. Please check back shortly for results.";
export const AUTO_TAGGING_SAVE_SUCCESS_TOAST = "Item saved successfully.";
export const AUTO_TAGGING_DELETE_SUCCESS_TOAST = "Item delete successfully.";
export const AUTO_TAGGING_ACTIVATE_SUCCESS_TOAST = "item activated successfully";
export const AUTO_TAGGING_DEACTIVATE_SUCCESS_TOAST =
  "item deactivated successfully";

function normalizeAiPlanConfigurableConcept(
  raw: unknown,
): ConfigurableConcept | null {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    return { code: trimmed, name: formatConceptCodeToLabel(trimmed) };
  }

  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = String(
    record.code ?? record.concept_code ?? record.concept_name ?? "",
  ).trim();
  const name = String(
    record.name ?? record.label ?? record.display_name ?? code,
  ).trim();
  const finalCode = code || name;
  if (!finalCode) return null;
  const mappedLabel = formatConceptCodeToLabel(finalCode);
  // Prefer API display name; fall back to known-code labels, then raw code.
  const displayName =
    (name && name !== finalCode ? name : "") ||
    mappedLabel ||
    name ||
    finalCode;
  return {
    code: finalCode,
    name: displayName,
  };
}

function extractConfigurableConceptsPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["concepts", "items", "data"] as const;

  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }

  const nested = record.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>;
    for (const key of nestedKeys) {
      if (Array.isArray(nestedRecord[key])) return nestedRecord[key] as unknown[];
    }
  }

  return [];
}

export function normalizeConfigurableConcepts(
  payload: unknown,
): ConfigurableConcept[] {
  return extractConfigurableConceptsPayload(payload)
    .map(normalizeAiPlanConfigurableConcept)
    .filter((item): item is ConfigurableConcept => item !== null);
}

export function normalizeConfigurableConcept(
  raw: unknown,
): ConfigurableConcept | null {
  const normalized = normalizeAiPlanConfigurableConcept(raw);
  if (!normalized) return null;
  return {
    ...normalized,
    display_name:
      typeof (raw as Record<string, unknown>)?.display_name === "string"
        ? String((raw as Record<string, unknown>).display_name).trim()
        : normalized.code.replace(/([A-Z])/g, " $1").trim() || normalized.name,
  };
}

export function parseConfigurableConceptsPayload(
  payload: unknown,
): ConfigurableConcept[] {
  return extractConfigurableConceptsPayload(payload)
    .map(normalizeConfigurableConcept)
    .filter((item): item is ConfigurableConcept => item !== null);
}

export async function fetchConfigurableConcepts(
  targetType: string,
  subType?: string | null,
): Promise<ConfigurableConcept[]> {
  if (USE_AUTO_TAGGING_MOCK) {
    return mockFetchConfigurableConcepts();
  }
  const params = buildConceptQueryParams(targetType, subType);
  if (!params) {
    return [];
  }

  const result = await apiClient.get<unknown>(
    "/auto-tagging/plans/configurable-concepts",
    params,
  );
  if (result.status !== "success") {
    return [];
  }

  return parseConfigurableConceptsPayload(result.data);
}

/** Concepts available as Then-step output tags for conditional (enhanced) plans. */
export async function fetchConditionalPlanOutputConcepts(
  targetType: string,
  subType?: string | null,
): Promise<ConfigurableConcept[]> {
  const params = buildConceptQueryParams(targetType, subType);
  if (!params) {
    return [];
  }

  const result = await apiClient.get<unknown>(
    "/auto-tagging/conditional-plans/output-concepts",
    params,
  );
  if (result.status !== "success") {
    return [];
  }

  return normalizeConfigurableConcepts(result.data);
}

export type ConditionalPlanCreateRequest = {
  target_type: string;
  sub_type: string;
  condition: {
    combinator: string;
    rules: unknown[];
    not: boolean;
  };
  concept: string;
  concept_value: {
    name: string;
    code: string;
    description: string;
  };
};

/** Create a single enhanced tagging rule (conditional plan). */
export async function createConditionalPlan(
  payload: ConditionalPlanCreateRequest,
): Promise<ApiResponse<unknown>> {
  const result = await apiClient.post<unknown>(
    "/auto-tagging/conditional-plans",
    payload,
  );
  if (result.status !== "success") {
    const err = new Error(
      formatConditionalPlanMutationError(result, "create"),
    ) as Error & { responseData?: unknown };
    err.responseData = result;
    throw err;
  }
  return result;
}

/** Update an existing enhanced tagging rule (conditional plan). */
export async function updateConditionalPlan(
  id: string,
  payload: ConditionalPlanCreateRequest,
): Promise<ApiResponse<unknown>> {
  const trimmedId = id.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
    payload,
  );
  if (result.status !== "success") {
    const err = new Error(
      formatConditionalPlanMutationError(result, "update"),
    ) as Error & { responseData?: unknown };
    err.responseData = result;
    throw err;
  }
  return result;
}

function readExistingPlanIdFromMutationPayload(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const record = payload as Record<string, unknown>;
  const errors = record.errors;
  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (!item || typeof item !== "object") continue;
      const details = (item as { details?: unknown }).details;
      if (!details || typeof details !== "object") continue;
      const id = String(
        (details as Record<string, unknown>).existing_plan_id ??
          (details as Record<string, unknown>).existingPlanId ??
          "",
      ).trim();
      if (id) return id;
    }
  }
  return String(
    record.existing_plan_id ?? record.existingPlanId ?? record.id ?? "",
  ).trim();
}

function formatConditionalPlanMutationError(
  result: ApiResponse<unknown>,
  action: "create" | "update",
): string {
  const fallback =
    action === "create"
      ? "Failed to create enhanced tagging rule."
      : "Failed to update enhanced tagging rule.";

  const payload = result.data ?? result;
  let fromErrors: string | null = null;
  if (payload && typeof payload === "object") {
    const errors = (payload as { errors?: unknown }).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const messages = errors
        .map((item) =>
          item && typeof item === "object"
            ? String((item as { message?: unknown }).message ?? "").trim()
            : "",
        )
        .filter(Boolean);
      if (messages.length > 0) fromErrors = messages.join("\n");
    }
  }

  const message = fromErrors || result.message?.trim() || fallback;
  const conflictId = readExistingPlanIdFromMutationPayload(payload);
  if (!conflictId) return message;
  if (
    /Rule\s*#?\s*[A-Za-z0-9_-]+/i.test(message) ||
    new RegExp(`\\b${conflictId}\\b`, "i").test(message)
  ) {
    return message;
  }
  return `${message} (Rule ${conflictId})`;
}

/** Toggle active on a conditional plan via PATCH (partial update). */
export async function updateConditionalPlanActive(
  planId: string,
  active: boolean,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
    { active },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${active ? "activate" : "deactivate"} enhanced tagging rule.`;
    throw new Error(message);
  }

  return result;
}

/** Delete a conditional plan (Enhanced Tagging Rule). */
export async function deleteConditionalPlan(
  planId: string,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Rule id is required.");
  }

  const result = await apiClient.delete<unknown>(
    `/auto-tagging/conditional-plans/${encodeURIComponent(trimmedId)}`,
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to delete enhanced tagging rule.";
    throw new Error(message);
  }

  return result;
}

export const CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE = 10;

export interface ConditionalPlanRecord {
  id: string;
  target_type: string;
  sub_type: string;
  concept: string;
  concept_value: AutoTaggingPlanSubmitConceptValue;
  active: boolean;
  /** Raw condition payload from the API (RQB-compatible group). */
  condition: unknown;
}

export interface FetchConditionalPlansParams {
  target_type?: string;
  sub_type?: string;
  concept?: string;
  concept_value_code?: string;
  page?: number;
  page_size?: number;
}

export interface FetchConditionalPlansResult {
  items: ConditionalPlanRecord[];
  total: number;
  page: number;
  pageSize: number;
}

function normalizeConditionalPlanConceptValue(
  raw: unknown,
): AutoTaggingPlanSubmitConceptValue {
  if (!raw || typeof raw !== "object") {
    return { name: "", code: "", description: "" };
  }
  const record = raw as Record<string, unknown>;
  return {
    name: String(record.name ?? "").trim(),
    code: String(record.code ?? "").trim(),
    description: String(record.description ?? "").trim(),
  };
}

function normalizeConditionalPlanRecord(
  raw: unknown,
  index: number,
): ConditionalPlanRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;

  const concept = String(
    record.concept ?? record.concept_code ?? record.conceptCode ?? "",
  ).trim();
  const conceptValue = normalizeConditionalPlanConceptValue(
    record.concept_value ?? record.conceptValue,
  );
  const id = String(
    record.id ?? record.plan_id ?? record.planId ?? record.uuid ?? index,
  ).trim();
  const targetType = normalizePlanTypeApiCode(
    String(
      record.target_type ??
        record.targetType ??
        record.object_type ??
        record.objectType ??
        "",
    ),
  );
  const subType = normalizePlanTypeApiCode(
    String(
      record.sub_type ??
        record.subType ??
        record.object_sub_type ??
        record.objectSubType ??
        "",
    ),
  );

  if (!id && !concept && !conceptValue.code) return null;

  return {
    id: id || `conditional-plan-${index}`,
    target_type: targetType,
    sub_type: subType,
    concept,
    concept_value: conceptValue,
    active: record.active !== false && record.enabled !== false,
    condition: record.condition ?? null,
  };
}

function extractConditionalPlansListPayload(payload: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }
  if (!payload || typeof payload !== "object") {
    return { items: [] };
  }

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["plans", "items", "results", "data"] as const;

  let items: unknown[] = [];
  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) {
      items = record[key] as unknown[];
      break;
    }
  }

  if (items.length === 0) {
    const nested = record.data;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const nestedRecord = nested as Record<string, unknown>;
      for (const key of nestedKeys) {
        if (Array.isArray(nestedRecord[key])) {
          items = nestedRecord[key] as unknown[];
          break;
        }
      }
    }
  }

  const total =
    typeof record.total === "number"
      ? record.total
      : typeof (record.data as Record<string, unknown> | undefined)?.total ===
          "number"
        ? ((record.data as Record<string, unknown>).total as number)
        : undefined;
  const page =
    typeof record.page === "number"
      ? record.page
      : typeof (record.data as Record<string, unknown> | undefined)?.page ===
          "number"
        ? ((record.data as Record<string, unknown>).page as number)
        : undefined;
  const pageSize =
    typeof record.page_size === "number"
      ? record.page_size
      : typeof (record.data as Record<string, unknown> | undefined)
            ?.page_size === "number"
        ? ((record.data as Record<string, unknown>).page_size as number)
        : undefined;

  return { items, total, page, pageSize };
}

export function normalizeConditionalPlans(
  payload: unknown,
): ConditionalPlanRecord[] {
  return extractConditionalPlansListPayload(payload)
    .items.map(normalizeConditionalPlanRecord)
    .filter((item): item is ConditionalPlanRecord => item !== null);
}

export function buildConditionalPlansQuery(options: {
  objectType?: string;
  objectSubType?: string;
  concept?: string;
  conceptValueCode?: string;
  page?: number;
  pageSize?: number;
}): FetchConditionalPlansParams {
  const params: FetchConditionalPlansParams = {
    page: options.page ?? 1,
    page_size: options.pageSize ?? CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE,
  };
  // UI Object Type / Object Sub-Type → API `target_type` / `sub_type`.
  const targetType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (targetType) params.target_type = targetType;
  const subType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (subType) params.sub_type = subType;
  const concept = options.concept?.trim();
  if (concept) params.concept = concept;
  const valueCode = options.conceptValueCode?.trim();
  if (valueCode) params.concept_value_code = valueCode;
  return params;
}

/** List conditional plans (enhanced tagging rules), newest first. */
export async function fetchConditionalPlans(
  params: FetchConditionalPlansParams = {},
): Promise<FetchConditionalPlansResult> {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE;
  const query: Record<string, string | number> = {
    page,
    page_size: pageSize,
  };
  if (params.target_type?.trim()) query.target_type = params.target_type.trim();
  if (params.sub_type?.trim()) query.sub_type = params.sub_type.trim();
  if (params.concept?.trim()) query.concept = params.concept.trim();
  if (params.concept_value_code?.trim()) {
    query.concept_value_code = params.concept_value_code.trim();
  }

  try {
    const result = await apiClient.get<unknown>(
      "/auto-tagging/conditional-plans",
      query,
    );
    if (result.status !== "success") {
      return { items: [], total: 0, page, pageSize };
    }

    const extracted = extractConditionalPlansListPayload(result.data);
    const items = extracted.items
      .map(normalizeConditionalPlanRecord)
      .filter((item): item is ConditionalPlanRecord => item !== null);

    return {
      items,
      total: extracted.total ?? items.length,
      page: extracted.page ?? page,
      pageSize: extracted.pageSize ?? pageSize,
    };
  } catch (error) {
    console.error("Failed to fetch conditional plans:", error);
    return { items: [], total: 0, page, pageSize };
  }
}

export function mapConditionalPlanToRuleRow(
  plan: ConditionalPlanRecord,
  options?: {
    conditionDescription?: string;
    planTypes?: AutoTaggingPlanTypeOption[];
  },
): {
  id: string;
  objectType: string;
  objectSubType: string;
  objectTypeName: string;
  objectSubTypeName: string;
  dimension: string;
  value: string;
  conditionDescription: string;
  enabled: boolean;
  conceptCode: string;
  conceptValueCode: string;
  conceptValueName: string;
  condition: unknown;
} {
  const planTypes = options?.planTypes ?? [];
  const valueLabel =
    plan.concept_value.name.trim() || plan.concept_value.code.trim();
  return {
    id: plan.id,
    objectType: plan.target_type,
    objectSubType: plan.sub_type,
    objectTypeName: resolvePlanTypeDisplayName(planTypes, plan.target_type),
    objectSubTypeName: resolvePlanSubTypeDisplayName(
      planTypes,
      plan.target_type,
      plan.sub_type,
    ),
    dimension: formatConceptCodeToLabel(plan.concept) || plan.concept,
    value: valueLabel,
    conditionDescription: options?.conditionDescription ?? "",
    enabled: plan.active,
    conceptCode: plan.concept,
    conceptValueCode: plan.concept_value.code,
    conceptValueName: plan.concept_value.name.trim() || plan.concept_value.code,
    condition: plan.condition,
  };
}

const defaultPromptCache = new Map<string, string>();
const defaultPromptInflight = new Map<string, Promise<string>>();

function normalizeDefaultPromptPayload(payload: unknown): string {
  if (typeof payload === "string") return payload.trim();
  if (!payload || typeof payload !== "object") return "";

  const record = payload as Record<string, unknown>;
  return String(
    record.prompt ??
      record.default_prompt ??
      record.defaultPrompt ??
      record.assessment_prompt ??
      record.assessmentPrompt ??
      "",
  ).trim();
}

function buildDefaultPromptCacheKey(
  conceptCode: string,
  targetType: string,
  subType: string,
): string {
  return `${targetType}|${subType}|${conceptCode}`;
}

export type FetchDefaultAutoTaggingPlanPromptOptions = {
  targetType?: string;
  subType?: string;
};

export async function fetchDefaultAutoTaggingPlanPrompt(
  conceptCode: string,
  options?: FetchDefaultAutoTaggingPlanPromptOptions,
): Promise<string> {
  const normalizedConceptCode = conceptCode.trim();
  if (!normalizedConceptCode) return "";

  const targetType =
    normalizePlanTypeApiCode(options?.targetType ?? "") ||
    AI_PLAN_DEFAULT_OBJECT_TYPE_CODE;
  const subType =
    normalizePlanTypeApiCode(options?.subType ?? "") ||
    (targetType === AI_PLAN_DEFAULT_OBJECT_TYPE_CODE
      ? AI_PLAN_DEFAULT_SUB_TYPE_CODE
      : "");

  const cacheKey = buildDefaultPromptCacheKey(
    normalizedConceptCode,
    targetType,
    subType,
  );
  const cached = defaultPromptCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const inflight = defaultPromptInflight.get(cacheKey);
  if (inflight) return inflight;

  const request = apiClient
    .get<unknown>("/auto-tagging/plans/default-prompt", {
      concept: normalizedConceptCode,
      target_type: targetType,
      ...(subType ? { sub_type: subType } : {}),
    })
    .then((result) => {
      if (result.status !== "success") {
        throw new Error(
          result.message?.trim() || "Failed to load default assessment prompt.",
        );
      }

      // Non-(insight, regulation) types return an empty string (not 422).
      const prompt = normalizeDefaultPromptPayload(result.data);
      defaultPromptCache.set(cacheKey, prompt);
      return prompt;
    })
    .finally(() => {
      defaultPromptInflight.delete(cacheKey);
    });

  defaultPromptInflight.set(cacheKey, request);
  return request;
}

export function __resetDefaultPromptCacheForTests(): void {
  defaultPromptCache.clear();
  defaultPromptInflight.clear();
}

export interface AutoTaggingPlanSubmitConceptValue {
  name: string;
  code: string;
  description: string;
}

export interface AutoTaggingPlanRecord {
  id: string;
  target_type: string;
  sub_type: string;
  concept: string;
  all_values: boolean;
  concept_values: AutoTaggingPlanSubmitConceptValue[];
  active: boolean;
  prompt: string;
}

export interface FetchAutoTaggingPlansParams {
  target_type?: string;
  sub_type?: string;
  concept?: string;
  page?: number;
  page_size?: number;
}

export const AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE = 10;

function normalizeConceptValueForList(
  raw: unknown,
): AutoTaggingPlanSubmitConceptValue | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const code = String(record.code ?? "").trim();
  if (!code) return null;
  return {
    code,
    name: String(record.name ?? ""),
    description: String(record.description ?? ""),
  };
}

function normalizeAutoTaggingPlanRecord(
  raw: unknown,
  index: number,
): AutoTaggingPlanRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;

  const targetType = String(
    record.target_type ?? record.targetType ?? record.object_type ?? record.objectType ?? "",
  ).trim();
  const subType = String(
    record.sub_type ?? record.subType ?? record.object_sub_type ?? record.objectSubType ?? "",
  ).trim();
  const concept = String(record.concept ?? record.concept_code ?? record.conceptCode ?? "").trim();
  const prompt = String(
    record.prompt ?? record.assessment_prompt ?? record.assessmentPrompt ?? record.description ?? "",
  ).trim();

  if (!targetType && !subType && !concept && !prompt) return null;

  const rawValues = record.concept_values ?? record.conceptValues ?? record.values;
  const conceptValues = Array.isArray(rawValues)
    ? rawValues
        .map(normalizeConceptValueForList)
        .filter((item): item is AutoTaggingPlanSubmitConceptValue => item !== null)
    : [];

  const id = String(record.id ?? record.plan_id ?? record.planId ?? record.uuid ?? index).trim();

  return {
    id: id || `plan-${index}`,
    target_type: targetType,
    sub_type: subType,
    concept,
    all_values: Boolean(record.all_values ?? record.allValues),
    concept_values: conceptValues,
    active: record.active !== false && record.enabled !== false,
    prompt,
  };
}

function extractPlansListPayload(payload: unknown): unknown[] {
  return extractPlansListMeta(payload).items;
}

function extractPlansListMeta(payload: unknown): {
  items: unknown[];
  total: number;
} {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }
  if (!payload || typeof payload !== "object") {
    return { items: [], total: 0 };
  }

  const record = payload as Record<string, unknown>;
  const nestedKeys = ["plans", "items", "results", "data"] as const;

  let items: unknown[] = [];
  for (const key of nestedKeys) {
    if (Array.isArray(record[key])) {
      items = record[key] as unknown[];
      break;
    }
  }

  if (items.length === 0) {
    const nested = record.data;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const nestedRecord = nested as Record<string, unknown>;
      for (const key of nestedKeys) {
        if (Array.isArray(nestedRecord[key])) {
          items = nestedRecord[key] as unknown[];
          break;
        }
      }
    }
  }

  const nestedData =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : undefined;
  const total =
    typeof record.total === "number"
      ? record.total
      : typeof nestedData?.total === "number"
        ? nestedData.total
        : items.length;

  return { items, total };
}

export type FetchStartAutoTaggingPlansResult = {
  items: AutoTaggingPlan[];
  total: number;
};

/**
 * Paginated plans for Start Auto-Tagging (Regulations list dialog).
 * API `page` is 1-based; defaults to insight / regulation filters.
 */
/** Default page size for Start Auto-Tagging dialog (backend max is 100). */
export const START_AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE = 10;

export async function fetchStartAutoTaggingPlans(options?: {
  page?: number;
  pageSize?: number;
  /**
   * Assessment Template exact match → API `assessment_type`
   * (e.g. tagging / implementation). Omit / empty = all templates.
   */
  assessmentType?: string;
}): Promise<FetchStartAutoTaggingPlansResult> {
  const page = Math.max(1, options?.page ?? 1);
  const pageSize = Math.min(
    100,
    Math.max(1, options?.pageSize ?? START_AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE),
  );
  const assessmentType = normalizeAssessmentTypeApiCode(
    options?.assessmentType ?? "",
  );

  if (USE_AUTO_TAGGING_MOCK) {
    const all = (
      await mockFetchAutoTaggingPlans(AI_PLAN_DEFAULT_OBJECT_TYPE_CODE)
    ).filter(
      (plan) =>
        !assessmentType ||
        normalizeAssessmentTypeApiCode(plan.assessment_type ?? "") ===
          assessmentType,
    );
    const start = (page - 1) * pageSize;
    return {
      items: all.slice(start, start + pageSize),
      total: all.length,
    };
  }

  const result = await apiClient.get<unknown>("/auto-tagging/plans", {
    target_type: AI_PLAN_DEFAULT_OBJECT_TYPE_CODE,
    sub_type: AI_PLAN_DEFAULT_SUB_TYPE_CODE,
    page,
    page_size: pageSize,
    ...(assessmentType ? { assessment_type: assessmentType } : {}),
  });
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Failed to load assessment plans.");
  }

  const { items: rawItems, total } = extractPlansListMeta(result.data);
  const items = rawItems
    .map(toRegulationsListPlan)
    .filter((item): item is AutoTaggingPlan => item !== null);

  return { items, total };
}

/**
 * Load every Start-dialog plan id across pages (API page_size max 100).
 * Used for header select-all so Start can send the full `plan_ids` list.
 */
export async function fetchAllStartAutoTaggingPlanIds(): Promise<string[]> {
  const pageSize = 100;
  const ids: string[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (ids.length < total) {
    const { items, total: nextTotal } = await fetchStartAutoTaggingPlans({
      page,
      pageSize,
    });
    total = Math.max(0, nextTotal);
    for (const plan of items) {
      const id = plan.id.trim();
      if (id) ids.push(id);
    }
    if (items.length === 0 || ids.length >= total) break;
    page += 1;
    if (page > 500) break;
  }

  return ids;
}

function extractAutoTaggingPlansPage(payload: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length };
  }
  if (!payload || typeof payload !== "object") {
    return { items: [] };
  }

  const record = payload as Record<string, unknown>;
  const items = extractPlansListPayload(payload);
  const nested =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : undefined;

  const total =
    typeof record.total === "number"
      ? record.total
      : typeof nested?.total === "number"
        ? nested.total
        : undefined;
  const page =
    typeof record.page === "number"
      ? record.page
      : typeof nested?.page === "number"
        ? nested.page
        : undefined;
  const pageSize =
    typeof record.page_size === "number"
      ? record.page_size
      : typeof nested?.page_size === "number"
        ? nested.page_size
        : undefined;

  return { items, total, page, pageSize };
}

export function normalizeAutoTaggingPlans(payload: unknown): AutoTaggingPlanRecord[] {
  return extractPlansListPayload(payload)
    .map(normalizeAutoTaggingPlanRecord)
    .filter((item): item is AutoTaggingPlanRecord => item !== null);
}

export type FetchAutoTaggingPlansResult = {
  items: AutoTaggingPlanRecord[];
  total: number;
  page: number;
  pageSize: number;
};

export function formatAutoTaggingPlanValues(plan: AutoTaggingPlanRecord): string {
  if (plan.all_values) return "All Values";
  if (plan.concept_values.length === 0) return "";
  return plan.concept_values
    .map((value) => value.name.trim() || value.code)
    .join(", ");
}

export function buildAutoTaggingPlansQuery(options: {
  objectType?: string;
  objectSubType?: string;
  concept?: string;
  page?: number;
  pageSize?: number;
}): FetchAutoTaggingPlansParams {
  const params: FetchAutoTaggingPlansParams = {
    page: options.page ?? 1,
    page_size: options.pageSize ?? AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  };

  // UI Object Type / Object Sub-Type → API `target_type` / `sub_type`.
  const targetType = normalizePlanTypeApiCode(options.objectType ?? "");
  if (targetType) {
    params.target_type = targetType;
  }

  const subType = normalizePlanTypeApiCode(options.objectSubType ?? "");
  if (subType) {
    params.sub_type = subType;
  }

  const concept = options.concept?.trim() ?? "";
  if (concept) {
    params.concept = concept;
  }

  return params;
}

async function fetchAiPlanAutoTaggingPlans(
  params: FetchAutoTaggingPlansParams,
): Promise<FetchAutoTaggingPlansResult> {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE;
  try {
    const result = await apiClient.get<unknown>("/auto-tagging/plans", {
      ...params,
      page,
      page_size: pageSize,
    });
    if (result.status !== "success") {
      return { items: [], total: 0, page, pageSize };
    }

    const extracted = extractAutoTaggingPlansPage(result.data);
    const items = extracted.items
      .map(normalizeAutoTaggingPlanRecord)
      .filter((item): item is AutoTaggingPlanRecord => item !== null);

    return {
      items,
      total: extracted.total ?? items.length,
      page: extracted.page ?? page,
      pageSize: extracted.pageSize ?? pageSize,
    };
  } catch (error) {
    console.error("Failed to fetch auto-tagging plans:", error);
    return { items: [], total: 0, page, pageSize };
  }
}

function normalizePlanId(raw: unknown, fallbackIndex: number): string | null {
  if (raw == null || raw === "") {
    return `plan-${fallbackIndex}`;
  }
  const id = String(raw).trim();
  return id || null;
}

function normalizeReferenceItem(
  raw: unknown,
): AutoTaggingPlanReferenceItem | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const concept = String(record.concept ?? "").trim();
  if (!concept) return null;
  const allValues = Boolean(record.all_values ?? record.allValues);
  const rawValues = record.values ?? record.concept_values ?? record.conceptValues;
  const values = Array.isArray(rawValues)
    ? rawValues
        .filter((value): value is Record<string, unknown> =>
          Boolean(value && typeof value === "object"),
        )
        .map((value) => ({
          code: String(value.code ?? "").trim(),
          name: String(value.name ?? "").trim(),
          description:
            value.description == null ? undefined : String(value.description),
        }))
        .filter((value) => value.code.length > 0)
    : [];
  if (!allValues && values.length === 0) return null;
  const displayName = String(
    record.display_name ?? record.displayName ?? "",
  ).trim();
  return {
    concept,
    ...(displayName ? { display_name: displayName } : {}),
    all_values: allValues,
    values: allValues ? [] : values,
  };
}

function normalizeReferenceItemsList(raw: unknown): AutoTaggingPlanReferenceItem[] {
  if (!Array.isArray(raw)) return [];
  const items: AutoTaggingPlanReferenceItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const item = normalizeReferenceItem(entry);
    if (!item) continue;
    const key = item.concept.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(item);
  }
  return items;
}

/** Normalize UI Assessment Template label or API code to snake_case API code. */
export function normalizeAssessmentTypeApiCode(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return "";
  if (trimmed === "tagging" || trimmed === "implementation") return trimmed;
  return trimmed.replace(/\s+/g, "_");
}

/** API `assessment_type` → UI Assessment Template label. */
export function formatAssessmentTemplateLabel(assessmentType: string): string {
  const code = normalizeAssessmentTypeApiCode(assessmentType);
  if (code === "tagging") return "Tagging";
  if (code === "implementation") return "Implementation";
  if (!code) return "";
  return code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, " ");
}

/** Homepage-style Impact item: `Topic = A, B` (or `Topic = All`). */
export function formatPlanImpactItemDisplay(plan: AutoTaggingPlan): string {
  const dimension =
    plan.concept_display_name?.trim() || plan.concept?.trim() || "";
  const valuesText = plan.all_values
    ? "All"
    : plan.concept_values
        .map((v) => v.name.trim() || v.code)
        .filter(Boolean)
        .join(", ");
  if (!dimension) return valuesText;
  if (!valuesText) return dimension;
  return `${dimension} = ${valuesText}`;
}

/** Compact Reference Items label: `R1, R2, …`. */
export function formatPlanReferenceItemsLabel(plan: AutoTaggingPlan): string {
  const count = plan.reference_items?.length ?? 0;
  if (count <= 0) return "";
  return Array.from({ length: count }, (_, index) => `R${index + 1}`).join(", ");
}

function toRegulationsListPlan(raw: unknown, index: number): AutoTaggingPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = normalizePlanId(record.id ?? record.plan_id, index);
  if (!id) return null;
  const concept_values = Array.isArray(record.concept_values)
    ? record.concept_values
        .filter((value): value is Record<string, unknown> =>
          Boolean(value && typeof value === "object"),
        )
        .map((value) => ({
          code: String(value.code ?? ""),
          name: String(value.name ?? ""),
          description:
            value.description == null ? undefined : String(value.description),
        }))
    : [];
  const name = String(record.name ?? record.assessment_name ?? "").trim();
  const assessmentType = normalizeAssessmentTypeApiCode(
    String(record.assessment_type ?? record.assessmentType ?? ""),
  );
  return {
    id,
    target_type: String(record.target_type ?? ""),
    sub_type: String(record.sub_type ?? "") || undefined,
    concept: String(record.concept ?? ""),
    concept_display_name:
      typeof record.concept_display_name === "string"
        ? record.concept_display_name
        : undefined,
    all_values: Boolean(record.all_values),
    concept_values,
    prompt: String(record.prompt ?? ""),
    active: record.active !== false,
    name,
    assessment_type: assessmentType,
    reference_items: normalizeReferenceItemsList(
      record.reference_items ?? record.referenceItems,
    ),
  };
}

export function fetchAutoTaggingPlans(
  targetType: string | null,
): Promise<AutoTaggingPlan[]>;
export function fetchAutoTaggingPlans(
  params: FetchAutoTaggingPlansParams,
): Promise<FetchAutoTaggingPlansResult>;
export async function fetchAutoTaggingPlans(
  input: string | null | FetchAutoTaggingPlansParams,
): Promise<AutoTaggingPlan[] | FetchAutoTaggingPlansResult> {
  if (typeof input === "object" && input !== null) {
    return fetchAiPlanAutoTaggingPlans(input);
  }
  if (USE_AUTO_TAGGING_MOCK) {
    return mockFetchAutoTaggingPlans(input ?? "");
  }

  const targetType =
    (typeof input === "string" ? input : "").trim() ||
    AI_PLAN_DEFAULT_OBJECT_TYPE_CODE;

  const result = await apiClient.get<unknown>("/auto-tagging/plans", {
    target_type: targetType,
    sub_type: AI_PLAN_DEFAULT_SUB_TYPE_CODE,
  });
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Failed to load assessment plans.");
  }
  return extractPlansListPayload(result.data)
    .map(toRegulationsListPlan)
    .filter((item): item is AutoTaggingPlan => item !== null);
}

export interface AutoTaggingPlanSubmitItem {
  target_type: string;
  sub_type: string;
  concept: string;
  all_values: boolean;
  concept_values: AutoTaggingPlanSubmitConceptValue[];
  active: boolean;
  /** Empty / unset assessment prompt is sent as null. */
  prompt: string | null;
}

export interface ConceptStatsRequest {
  target_type: string;
  concept: string;
  sub_type: string;
}

export interface ConceptStatsData {
  target_type: string;
  sub_type: string;
  concept: string;
  current_count: number;
  plan_ids: string[];
  limit: number;
  available: number;
  can_create: boolean;
}

export function buildConceptComboKey(item: {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
}): string {
  if (!item.objectType.trim() || !item.conceptCode.trim()) {
    return "";
  }

  return [
    normalizePlanTypeApiCode(item.objectType),
    normalizePlanTypeApiCode(item.objectSubType),
    item.conceptCode.trim(),
  ].join("|");
}

export function countItemsWithSameConceptCombo<
  T extends {
    objectType: string;
    objectSubType: string;
    conceptCode: string;
  },
>(items: readonly T[], target: T): number {
  const key = buildConceptComboKey(target);
  if (!key) return 0;
  return items.filter((item) => buildConceptComboKey(item) === key).length;
}

export function parseConceptStatsData(
  response: ApiResponse<unknown>,
): ConceptStatsData | null {
  if (response.status !== "success" || !response.data) return null;
  if (typeof response.data !== "object") return null;

  const record = response.data as Record<string, unknown>;
  return {
    target_type: String(record.target_type ?? ""),
    sub_type: String(record.sub_type ?? ""),
    concept: String(record.concept ?? ""),
    current_count: Number(record.current_count ?? 0),
    plan_ids: Array.isArray(record.plan_ids)
      ? record.plan_ids
          .map((id) => String(id ?? "").trim())
          .filter((id) => id.length > 0)
      : [],
    limit: Number(record.limit ?? 0),
    available: Number(record.available ?? 0),
    can_create: record.can_create !== false,
  };
}

export function buildConceptStatsHintMessage(
  stats: ConceptStatsData,
  sameComboDraftCount = 1,
): string {
  const remaining = stats.available;

  if (!stats.can_create || remaining <= 0) {
    return `The maximum of ${stats.limit} plans for this combination has been reached (${stats.limit}/${stats.limit} used).`;
  }

  if (sameComboDraftCount > remaining) {
    return `Only ${remaining} plan slot${remaining === 1 ? "" : "s"} remaining for this combination, but this form has ${sameComboDraftCount} item(s) with the same selection.`;
  }

  if (sameComboDraftCount > 1) {
    return `You can add ${remaining} more plan${remaining === 1 ? "" : "s"} for this combination (${stats.current_count}/${stats.limit} used). This form contains ${sameComboDraftCount} item(s) with the same selection.`;
  }

  return `You can add ${remaining} more plan${remaining === 1 ? "" : "s"} for this combination (${stats.current_count}/${stats.limit} used).`;
}

export function buildConceptStatsExceededMessage(
  stats: ConceptStatsData,
  requestedCount: number,
): string {
  if (!stats.can_create || stats.available <= 0) {
    return `The maximum of ${stats.limit} plans for this combination has been reached (${stats.limit}/${stats.limit} used).`;
  }

  return `Only ${stats.available} plan slot${stats.available === 1 ? "" : "s"} remaining for this combination, but you are trying to add ${requestedCount} item(s) with the same selection.`;
}

export function isConceptStatsExceeded(
  stats: ConceptStatsData,
  requestedCount: number,
): boolean {
  return !stats.can_create || requestedCount > stats.available;
}

function mapConceptValueForSubmit(value: {
  code: string;
  name?: string;
  description?: string;
}): AutoTaggingPlanSubmitConceptValue {
  return {
    name: String(value.name ?? ""),
    code: value.code,
    description: String(value.description ?? ""),
  };
}

export function buildAutoTaggingPlanSubmitItem(item: {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValues: Array<{
    code: string;
    name?: string;
    description?: string;
  }>;
  assessmentPrompt: string;
  allValues?: boolean;
  active?: boolean;
}): AutoTaggingPlanSubmitItem {
  const allValues = item.allValues ?? false;
  const prompt = item.assessmentPrompt.trim();
  return {
    target_type: normalizePlanTypeApiCode(item.objectType),
    sub_type: normalizePlanTypeApiCode(item.objectSubType),
    concept: item.conceptCode,
    all_values: allValues,
    concept_values: allValues
      ? []
      : item.conceptValues.map(mapConceptValueForSubmit),
    active: item.active ?? true,
    prompt: prompt || null,
  };
}

export interface ImportedAssessmentPlanFormItem {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValues: Array<{
    id: string;
    code: string;
    name: string;
    description?: string;
  }>;
  assessmentPrompt: string;
  allValues: boolean;
}

export function mapImportedPlanToFormItem(
  plan: AutoTaggingPlanRecord,
): ImportedAssessmentPlanFormItem {
  return {
    objectType: normalizePlanTypeApiCode(plan.target_type),
    objectSubType: normalizePlanTypeApiCode(plan.sub_type),
    conceptCode: plan.concept,
    conceptValues: plan.concept_values.map((value) => ({
      id: value.code,
      code: value.code,
      name: value.name.trim() || value.code,
      description: value.description,
    })),
    assessmentPrompt: plan.prompt,
    allValues: plan.all_values,
  };
}

export type AutoTaggingPlanImportRow = {
  row: number;
  status: string;
  plan: AutoTaggingPlanRecord | null;
  errors: string[];
};

export type AutoTaggingPlanImportResult = {
  rows: AutoTaggingPlanImportRow[];
  total: number;
  validCount: number;
  errorCount: number;
};

function normalizeImportErrorMessage(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    return String(
      record.message ?? record.error ?? record.detail ?? record.code ?? "",
    ).trim();
  }
  return String(raw ?? "").trim();
}

function formatImportRowErrors(rows: AutoTaggingPlanImportRow[]): string {
  const messages = rows
    .filter((item) => item.status !== "valid" || item.errors.length > 0)
    .map((item) => {
      const detail = item.errors.join("; ") || item.status || "invalid";
      // API row index is 0-based; show 1-based for users.
      return `Row ${item.row + 1}: ${detail}`;
    });

  if (messages.length === 0) {
    return "Import validation failed.";
  }

  const preview = messages.slice(0, 3).join(" ");
  if (messages.length <= 3) return preview;
  return `${preview} (+${messages.length - 3} more)`;
}

/** Parse POST /auto-tagging/plans/import `data` shape (`rows` + counts). */
export function normalizeAutoTaggingPlanImportPayload(
  payload: unknown,
): AutoTaggingPlanImportResult {
  if (!payload || typeof payload !== "object") {
    return { rows: [], total: 0, validCount: 0, errorCount: 0 };
  }

  const data = payload as Record<string, unknown>;
  const rawRows = Array.isArray(data.rows) ? data.rows : [];
  const rows: AutoTaggingPlanImportRow[] = rawRows.map((item, index) => {
    if (!item || typeof item !== "object") {
      return {
        row: index,
        status: "error",
        plan: null,
        errors: ["Invalid import row."],
      };
    }

    const record = item as Record<string, unknown>;
    const rowNumber = Number(record.row);
    const row = Number.isFinite(rowNumber) ? rowNumber : index;
    const status = String(record.status ?? "")
      .trim()
      .toLowerCase();
    const plan = normalizeAutoTaggingPlanRecord(record.plan, row);
    const errors = Array.isArray(record.errors)
      ? record.errors.map(normalizeImportErrorMessage).filter(Boolean)
      : [];

    return {
      row,
      status: status || (errors.length > 0 ? "error" : "valid"),
      plan,
      errors,
    };
  });

  const validCountRaw = Number(data.valid_count ?? data.validCount);
  const errorCountRaw = Number(data.error_count ?? data.errorCount);
  const totalRaw = Number(data.total);

  return {
    rows,
    total: Number.isFinite(totalRaw) ? totalRaw : rows.length,
    validCount: Number.isFinite(validCountRaw)
      ? validCountRaw
      : rows.filter((item) => item.status === "valid").length,
    errorCount: Number.isFinite(errorCountRaw)
      ? errorCountRaw
      : rows.filter((item) => item.status !== "valid").length,
  };
}

function extractImportedPlansFromPayload(
  payload: unknown,
): AutoTaggingPlanRecord[] {
  const importResult = normalizeAutoTaggingPlanImportPayload(payload);
  if (importResult.errorCount > 0) {
    throw new Error(formatImportRowErrors(importResult.rows));
  }

  const invalidRows = importResult.rows.filter(
    (item) => item.status !== "valid" || item.errors.length > 0,
  );
  if (invalidRows.length > 0) {
    throw new Error(formatImportRowErrors(invalidRows));
  }

  return importResult.rows
    .map((item) => item.plan)
    .filter((plan): plan is AutoTaggingPlanRecord => plan !== null);
}

export async function importAutoTaggingPlansFromFile(
  file: File,
): Promise<ImportedAssessmentPlanFormItem[]> {
  const formData = new FormData();
  formData.append("file", file);

  const result = await apiClient.post<unknown>(
    "/auto-tagging/plans/import",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to import assessment plan items.";
    throw new Error(message);
  }

  const plans = extractImportedPlansFromPayload(result.data);
  if (plans.length === 0) {
    throw new Error("No items found in the uploaded file.");
  }

  return plans.map(mapImportedPlanToFormItem);
}

export function buildConceptStatsRequest(item: {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
}): ConceptStatsRequest {
  return {
    target_type: normalizePlanTypeApiCode(item.objectType),
    concept: item.conceptCode,
    sub_type: normalizePlanTypeApiCode(item.objectSubType),
  };
}

export function isConceptStatsValidationPassed(
  response: ApiResponse<unknown>,
): boolean {
  if (response.status !== "success") return false;

  const stats = parseConceptStatsData(response);
  if (stats) {
    return stats.can_create && stats.available > 0;
  }

  if (!response.data || typeof response.data !== "object") return true;

  const record = response.data as Record<string, unknown>;
  if (typeof record.can_create === "boolean") return record.can_create;
  if (typeof record.valid === "boolean") return record.valid;
  if (typeof record.is_valid === "boolean") return record.is_valid;
  if (typeof record.passed === "boolean") return record.passed;

  return true;
}

export async function fetchConceptStats(
  params: ConceptStatsRequest,
): Promise<ApiResponse<unknown>> {
  return apiClient.get<unknown>("/auto-tagging/plans/concept-stats", params);
}

export async function validateConceptStatsForItem(item: {
  objectType: string;
  objectSubType: string;
  conceptCode: string;
}): Promise<void> {
  await validateDraftItemsConceptStatsAvailability([item]);
}

export async function validateDraftItemsConceptStatsAvailability(
  items: Array<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
  }>,
): Promise<void> {
  const groups = new Map<
    string,
    Array<{
      objectType: string;
      objectSubType: string;
      conceptCode: string;
    }>
  >();

  items.forEach((item) => {
    const key = buildConceptComboKey(item);
    if (!key) return;
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  });

  for (const groupItems of groups.values()) {
    const statsResponse = await fetchConceptStats(
      buildConceptStatsRequest(groupItems[0]),
    );
    const stats = parseConceptStatsData(statsResponse);
    if (!stats) {
      const message =
        statsResponse.message?.trim() ||
        "Failed to check plan availability.";
      throw new Error(message);
    }

    if (isConceptStatsExceeded(stats, groupItems.length)) {
      throw new Error(
        buildConceptStatsExceededMessage(stats, groupItems.length),
      );
    }
  }
}

export async function submitAutoTaggingPlans(
  plans: AutoTaggingPlanSubmitItem[],
): Promise<ApiResponse<unknown>> {
  return apiClient.post<unknown>("/auto-tagging/plans", { plans });
}

export async function deleteAutoTaggingPlan(
  planId: string | number,
): Promise<ApiResponse<unknown> | string> {
  const trimmedId = String(planId).trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  if (USE_AUTO_TAGGING_MOCK) {
    return mockDeleteAutoTaggingPlan(trimmedId);
  }

  const result = await apiClient.delete<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
  );
  if (result.status !== "success") {
    const message = result.message?.trim() || "Failed to delete tagging plan.";
    throw new Error(message);
  }

  return result;
}

export async function updateAutoTaggingPlanActive(
  planId: string,
  active: boolean,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    { active },
  );
  if (result.status !== "success") {
    const message =
      result.message?.trim() ||
      `Failed to ${active ? "activate" : "deactivate"} tagging plan.`;
    throw new Error(message);
  }

  return result;
}

export async function updateAutoTaggingPlan(
  planId: string,
  payload: AutoTaggingPlanSubmitItem,
): Promise<ApiResponse<unknown>> {
  const trimmedId = planId.trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }

  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    payload,
  );
  if (result.status !== "success") {
    const message = result.message?.trim() || "Failed to update tagging plan.";
    throw new Error(message);
  }

  return result;
}

export async function submitAssessmentPlanItems(
  items: Array<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
    conceptValues: Array<{
      code: string;
      name?: string;
      description?: string;
    }>;
    assessmentPrompt: string;
    allValues?: boolean;
    active?: boolean;
  }>,
): Promise<ApiResponse<unknown>> {
  const plans = items.map((item) => buildAutoTaggingPlanSubmitItem(item));

  await validateDraftItemsConceptStatsAvailability(items);

  const submitResponse = await submitAutoTaggingPlans(plans);
  if (submitResponse.status !== "success") {
    const message =
      submitResponse.message?.trim() || "Failed to submit assessment plan items.";
    throw new Error(message);
  }

  return submitResponse;
}

export interface AutoTaggingPlansExportPayload {
  file_path?: string;
  presigned_url: string;
  expires_in?: number;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function filenameFromExportPath(filePath: string | undefined): string | null {
  if (!filePath?.trim()) return null;
  const segment = filePath.split("/").filter(Boolean).pop();
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function buildAutoTaggingPlansExportFilename(filePath?: string): string {
  const dateStr = new Date().toISOString().slice(0, 10);
  const fromPath = filenameFromExportPath(filePath);
  if (fromPath) {
    const baseName = fromPath.replace(/\.[^.]+$/, "");
    return `${baseName || fromPath}.csv`;
  }
  return `auto-tagging-plan-export-${dateStr}.csv`;
}

function ensureCsvFilename(filename: string): string {
  if (/\.csv$/i.test(filename)) return filename;
  const baseName = filename.replace(/\.[^.]+$/, "");
  return `${baseName || filename}.csv`;
}

function parseFilenameFromContentDisposition(header: string): string | null {
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename=([^;\s]+)/i.exec(header);
  if (plain?.[1]) return plain[1].replace(/(?:^"|"$)/g, "");
  return null;
}

function extractAutoTaggingPlansExportPayload(
  payload: unknown,
): AutoTaggingPlansExportPayload {
  if (!payload || typeof payload !== "object") {
    throw new Error("Export failed: invalid response.");
  }

  const root = payload as Record<string, unknown>;
  let node: Record<string, unknown> = root;
  if ("data" in root && root.data && typeof root.data === "object") {
    node = root.data as Record<string, unknown>;
  }

  const presignedUrl = node.presigned_url;
  if (typeof presignedUrl !== "string" || !presignedUrl.trim()) {
    const message =
      typeof root.message === "string" && root.message.trim()
        ? root.message.trim()
        : "Export failed: download URL is missing.";
    throw new Error(message);
  }

  return {
    file_path: typeof node.file_path === "string" ? node.file_path : undefined,
    presigned_url: presignedUrl.trim(),
    expires_in:
      typeof node.expires_in === "number" ? node.expires_in : undefined,
  };
}

async function downloadExportFromPresignedUrl(
  presignedUrl: string,
  fallbackFilename: string,
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  });
  if (!response.ok) {
    let detail = "";
    try {
      detail = await response.text();
    } catch {
      /* ignore */
    }
    throw new Error(
      `Export download failed (${response.status}). ${detail.slice(0, 300)}`,
    );
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error("Export failed: empty file received.");
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  const filename = ensureCsvFilename(
    (contentDisposition
      ? parseFilenameFromContentDisposition(contentDisposition)
      : null) || fallbackFilename,
  );
  triggerBlobDownload(blob, filename);
}

export async function exportAutoTaggingPlans(): Promise<void> {
  const result = await apiClient.get<unknown>("/auto-tagging/plans/export");
  if (result.status !== "success") {
    const message =
      result.message?.trim() || "Failed to export assessment plan.";
    throw new Error(message);
  }

  const exportPayload = extractAutoTaggingPlansExportPayload(result.data ?? result);
  const fallbackFilename = buildAutoTaggingPlansExportFilename(
    exportPayload.file_path,
  );

  await downloadExportFromPresignedUrl(
    exportPayload.presigned_url,
    fallbackFilename,
  );
}

export async function patchAutoTaggingPlan(
  planId: string | number,
  payload: PatchAutoTaggingPlanPayload,
): Promise<AutoTaggingPlan> {
  const trimmedId = String(planId).trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }
  if (USE_AUTO_TAGGING_MOCK) {
    return mockPatchAutoTaggingPlan(trimmedId, payload);
  }
  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    payload,
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to update auto-tagging plan.");
  }
  const normalized = toRegulationsListPlan(result.data, 0);
  if (!normalized) {
    throw new Error("Failed to update auto-tagging plan.");
  }
  return normalized;
}

export async function patchAutoTaggingPlanActive(
  planId: string | number,
  active: boolean,
): Promise<AutoTaggingPlan> {
  const trimmedId = String(planId).trim();
  if (!trimmedId) {
    throw new Error("Plan id is required");
  }
  if (USE_AUTO_TAGGING_MOCK) {
    return mockPatchAutoTaggingPlanActive(trimmedId, active);
  }
  const result = await apiClient.patch<unknown>(
    `/auto-tagging/plans/${encodeURIComponent(trimmedId)}`,
    { active },
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to update auto-tagging plan.");
  }
  const normalized = toRegulationsListPlan(result.data, 0);
  if (!normalized) {
    throw new Error("Failed to update auto-tagging plan.");
  }
  return normalized;
}

export async function fetchDefaultPrompt(concept: string): Promise<string> {
  if (USE_AUTO_TAGGING_MOCK) return mockFetchDefaultPrompt(concept);
  return fetchDefaultAutoTaggingPlanPrompt(concept);
}

export async function triggerAutoTaggingBundleTask(
  request: AutoTaggingBundleTaskRequest,
): Promise<AutoTaggingBundleTaskResponse> {
  if (USE_AUTO_TAGGING_MOCK) return mockTriggerAutoTaggingBundleTask();
  const result = await apiClient.post<AutoTaggingBundleTaskResponse>(
    "/auto-tagging/bundle-task",
    request,
  );
  if (result.status !== "success" || !result.data) {
    throw new Error(result.message?.trim() || "Failed to start assessment.");
  }
  return result.data;
}

export function buildConceptDisplayNameMap(
  concepts: ConfigurableConcept[],
): Map<string, string> {
  const displayNameMap = new Map<string, string>();
  concepts.forEach((concept) => {
    const displayName = concept.display_name || concept.name;
    if (concept.code?.trim()) {
      displayNameMap.set(concept.code.trim(), displayName);
    }
    if (concept.name?.trim()) {
      displayNameMap.set(concept.name.trim(), displayName);
    }
  });
  return displayNameMap;
}

export function resolveConceptDisplayName(
  concept: string,
  displayNameMap: Map<string, string>,
): string {
  return (
    displayNameMap.get(concept) ??
    concept.replace(/([A-Z])/g, " $1").trim()
  );
}

export function enrichAutoTaggingPlans(
  plans: AutoTaggingPlan[],
  concepts: ConfigurableConcept[],
): AutoTaggingPlan[] {
  const displayNameMap = buildConceptDisplayNameMap(concepts);
  return plans.map((plan) => ({
    ...plan,
    concept_display_name: resolveConceptDisplayName(plan.concept, displayNameMap),
  }));
}

export function formatPlanValues(plan: AutoTaggingPlan): string {
  return plan.all_values
    ? "All"
    : plan.concept_values
        .map((v) => v.name.trim() || v.code)
        .filter(Boolean)
        .join(", ");
}

export function formatPlanTypeLabel(value: string | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : "";
}

export function normalizeRegulationVersion(version: string): string {
  const trimmed = version.trim();
  return trimmed.startsWith("v") ? trimmed.slice(1).trim() : trimmed;
}
